import {ellipsis} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Renderer} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  InteractiveListItem,
  InteractiveListItemPublicProps,
} from '../../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {renderHighlightedPart} from './inboxesSearchSuggestionsHelpers';

/*
 * Props.
 */

interface SearchSuggestionsDropdownItemProps extends InteractiveListItemPublicProps {
  prefix?: string | null;
  shouldBoldPrefix?: boolean;
  icon?: ReactNode;
  label: ReactNode;
  shouldItalicizeLabel?: boolean;
  renderLabelTooltip?: Renderer;
  suffix?: ReactNode;
  score?: number | undefined;
  fuzzyQuery: string;
  shouldDisplaySearchTokens?: boolean;
}

/*
 * Style.
 */

const StyledSearchSuggestionsDropdownItemDiv = styled.div<{
  $isHighlighted: boolean;
}>`
  ${flexCenter}
  gap: 4px;
  padding: 0 16px;
  height: 30px;
  ${(p) =>
    p.$isHighlighted &&
    css`
      background-color: ${p.theme.greys.shade10};
    `};
`;

const StyledSearchSuggestionDropdownItemLabelWrapperDiv = styled.div`
  ${flexCenter};
  overflow: hidden;
`;

const StyledSearchSuggestionDropdownItemLabelIconDiv = styled.div`
  margin-right: 4px;
`;

const StyledSearchSuggestionDropdownItemLabelTooltipCoordinator = styled(TooltipCoordinator)`
  min-width: 0;
  ${flexCenter}
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.normal};
`;

const StyledTooltipOverflowCondition = styled(TooltipOverflowCondition)<{$shouldItalicize: boolean}>`
  line-height: 30px;
  ${(p) =>
    p.$shouldItalicize &&
    css`
      font-style: italic;
    `}
`;

const StyledSearchSuggestionDropdownItemSubTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledSearchSuggestionDropdownItemPrefixSpan = styled(StyledSearchSuggestionDropdownItemSubTextSpan)<{
  $shouldBold: boolean;
}>`
  flex-shrink: 0;
  ${(p) =>
    p.$shouldBold &&
    css`
      font-weight: ${fontWeights.semibold};
    `}
`;

const StyledSearchSuggestionDropdownItemSuffixSpan = styled(StyledSearchSuggestionDropdownItemSubTextSpan)`
  ${ellipsis()}
`;

/*
 * Component.
 */

export const SearchSuggestionsDropdownItem: FC<SearchSuggestionsDropdownItemProps> = (props) => {
  const {
    prefix,
    shouldBoldPrefix,
    icon,
    label,
    shouldItalicizeLabel,
    renderLabelTooltip = () => label,
    suffix,
    score,
    fuzzyQuery,
    shouldDisplaySearchTokens,
    ...interativeListItemProps
  } = props;

  return (
    <InteractiveListItem {...interativeListItemProps}>
      {({itemProps, isHighlighted}) => (
        <StyledSearchSuggestionsDropdownItemDiv {...itemProps} $isHighlighted={isHighlighted}>
          {prefix && (
            <StyledSearchSuggestionDropdownItemPrefixSpan $shouldBold={Boolean(shouldBoldPrefix)}>
              {prefix}
            </StyledSearchSuggestionDropdownItemPrefixSpan>
          )}
          <StyledSearchSuggestionDropdownItemLabelWrapperDiv>
            {icon && (
              <StyledSearchSuggestionDropdownItemLabelIconDiv>
                {icon}
              </StyledSearchSuggestionDropdownItemLabelIconDiv>
            )}
            <StyledSearchSuggestionDropdownItemLabelTooltipCoordinator render={renderLabelTooltip}>
              <StyledTooltipOverflowCondition $shouldItalicize={Boolean(shouldItalicizeLabel)}>
                {renderHighlightedPart(label, undefined, fuzzyQuery, shouldDisplaySearchTokens)}
              </StyledTooltipOverflowCondition>
            </StyledSearchSuggestionDropdownItemLabelTooltipCoordinator>
          </StyledSearchSuggestionDropdownItemLabelWrapperDiv>
          {suffix && (
            <StyledSearchSuggestionDropdownItemSuffixSpan>
              {renderHighlightedPart(suffix, undefined, fuzzyQuery, shouldDisplaySearchTokens)}
            </StyledSearchSuggestionDropdownItemSuffixSpan>
          )}
        </StyledSearchSuggestionsDropdownItemDiv>
      )}
    </InteractiveListItem>
  );
};
